import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotify } from '@routes-plan/ui';
import { Country } from '../../../models/country';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { apiError } from '../shared/AdminUi';
import OfficeForm, { BusinessModelOption, OfficeFormErrors, OfficeFormValues, validateOffice } from './OfficeForm';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EMPTY: OfficeFormValues = {
  name: '',
  phone: '',
  address: '',
  city: '',
  countryId: '',
  businessModelId: '',
  shareClients: false,
};

const OfficeEdit: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canUpdate } = useResourcePermissions('/admin/offices');
  const [values, setValues] = useState<OfficeFormValues>(EMPTY);
  const [initial, setInitial] = useState<OfficeFormValues>(EMPTY);
  const [manager, setManager] = useState('');
  const [errors, setErrors] = useState<OfficeFormErrors>({});
  const [countries, setCountries] = useState<Country[]>([]);
  const [businessModels, setBusinessModels] = useState<BusinessModelOption[]>([]);
  const [saving, setSaving] = useState(false);

  setPageTitle('offices.editPageTitle');
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: countryData } = await axios.get('typecountry');
        setCountries(Array.isArray(countryData?.data) ? countryData.data : []);
        const { data: officeData } = await axios.get(`offices/${id}`);
        const { data: modelsData } = await axios.get('business-models');
        const selectable: BusinessModelOption[] = Array.isArray(modelsData?.data) ? modelsData.data : [];
        const currentModel = officeData?.businessModel as BusinessModelOption | null;
        // Si la sede tiene un tipo no ofrecido (p. ej. Mixto) se muestra igual para no perderlo.
        setBusinessModels(
          currentModel && !selectable.some((m) => m.id === currentModel.id) ? [...selectable, currentModel] : selectable,
        );
        const loaded: OfficeFormValues = {
          name: officeData?.name ?? '',
          phone: officeData?.phone ?? '',
          address: officeData?.address ?? '',
          city: officeData?.city ?? '',
          countryId: officeData?.country?.id != null ? String(officeData.country.id) : '',
          businessModelId: currentModel?.id ? String(currentModel.id) : '',
          shareClients: Boolean(officeData?.shareClients),
        };
        setManager(officeData?.manager ?? '');
        setInitial(loaded);
        setValues(loaded);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const dirty = (Object.keys(values) as (keyof OfficeFormValues)[]).some((k) => values[k] !== initial[k]);

  const submit = async () => {
    if (!canUpdate) return;
    const found = validateOffice(values, t('common.required'), t('offices.officeTypeRequired'));
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('offices.missingFields'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`offices/${id}`, {
        name: values.name,
        phone: values.phone,
        address: values.address,
        city: values.city,
        manager,
        countryId: values.countryId ? Number(values.countryId) : '',
        businessModelId: Number(values.businessModelId),
        shareClients: values.shareClients,
      });
      notify.success(t('offices.updatedOk'));
      navigate('/admin/offices');
    } catch (error) {
      notify.error(apiError(error, t('offices.updateError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OfficeForm
      mode="edit"
      values={values}
      errors={errors}
      countries={countries}
      businessModels={businessModels}
      onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
      onSubmit={submit}
      saving={saving}
      allowed={canUpdate}
      dirty={dirty}
    />
  );
};

export default OfficeEdit;
